import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut } from 'firebase/auth';
import { auth, db, storage } from '../../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../../utils/imageUtils';
import NurseBottomNavigation from '../Layout/NurseBottomNavigation';
import ThemeSelector from '../Theme/ThemeSelector';

const NurseSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, refreshUser } = useAuth();
  const { currentTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    email: '',
    phone: '',
    photoURL: ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Settings state
  const [settings, setSettings] = useState({
    notifications: true,
    language: 'th'
  });
  
  // UI state
  const [errors, setErrors] = useState({});
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadUserData();
    } else if (currentUser === null) {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadUserData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // Load from Firebase Auth
      const authUser = auth.currentUser;
      if (authUser) {
        setProfileForm({
          displayName: authUser.displayName || '',
          email: authUser.email || '',
          phone: '',
          photoURL: authUser.photoURL || ''
        });
      }

      // Load from Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setProfileForm(prev => ({
          ...prev,
          phone: userData.phone || userData.phoneNumber || '',
          photoURL: userData.photoURL || prev.photoURL
        }));
        setSettings({
          notifications: userData.notificationsEnabled !== false,
          language: userData.language || 'th'
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกรูปภาพเท่านั้น');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      return;
    }

    setUploading(true);
    try {
      const compressedFile = await compressImage(file, 0.8);
      const storageRef = ref(storage, `profile-images/${currentUser.uid}/${Date.now()}`);
      await uploadBytes(storageRef, compressedFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      setProfileForm(prev => ({
        ...prev,
        photoURL: downloadURL
      }));
      
      alert('อัปโหลดรูปภาพสำเร็จ');
    } catch (error) {
      if (error.code === 'storage/unauthorized') {
        alert('คุณไม่มีสิทธิ์อัปโหลดไฟล์');
      } else {
        alert('เกิดข้อผิดพลาดในการอัปโหลด: ' + error.message);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSettingsChange = (name, value) => {
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    const newErrors = {};
    
    if (!profileForm.displayName.trim()) {
      newErrors.displayName = 'กรุณากรอกชื่อ';
    }
    
    if (!profileForm.email.trim()) {
      newErrors.email = 'กรุณากรอกอีเมล';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const authUser = auth.currentUser;
      if (!authUser) {
        throw new Error('ไม่พบผู้ใช้');
      }

      // Update Firebase Auth profile
      const updates = {};
      if (authUser.displayName !== profileForm.displayName) {
        updates.displayName = profileForm.displayName;
      }
      if (authUser.photoURL !== profileForm.photoURL) {
        updates.photoURL = profileForm.photoURL;
      }
      
      if (Object.keys(updates).length > 0) {
        await updateProfile(authUser, updates);
      }

      // Update email if changed
      if (authUser.email !== profileForm.email) {
        if (window.confirm('การเปลี่ยนอีเมลจะต้องยืนยันอีเมลใหม่ คุณต้องการดำเนินการต่อหรือไม่?')) {
          await updateEmail(authUser, profileForm.email);
          alert('กรุณาตรวจสอบอีเมลเพื่อยืนยันอีเมลใหม่');
        }
      }

      // Update Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: profileForm.displayName,
        phone: profileForm.phone,
        photoURL: profileForm.photoURL,
        notificationsEnabled: settings.notifications,
        language: settings.language,
        updatedAt: new Date()
      });

      // Update language in i18n
      if (settings.language !== i18n.language) {
        i18n.changeLanguage(settings.language);
      }

      // Refresh user data
      await refreshUser();
      
      alert('บันทึกข้อมูลสำเร็จ');
      setErrors({});
    } catch (error) {
      if (error.code === 'auth/requires-recent-login') {
        alert('กรุณาเข้าสู่ระบบอีกครั้งเพื่อเปลี่ยนอีเมล');
      } else {
        alert('เกิดข้อผิดพลาดในการบันทึก: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentUser) return;

    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'กรุณากรอกรหัสผ่านปัจจุบัน';
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'กรุณากรอกรหัสผ่านใหม่';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร';
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'รหัสผ่านไม่ตรงกัน';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      const authUser = auth.currentUser;
      if (!authUser || !authUser.email) {
        throw new Error('ไม่พบผู้ใช้');
      }

      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        authUser.email,
        passwordForm.currentPassword
      );
      await reauthenticateWithCredential(authUser, credential);

      // Update password
      await updatePassword(authUser, passwordForm.newPassword);
      
      alert('เปลี่ยนรหัสผ่านสำเร็จ');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowChangePassword(false);
      setErrors({});
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setErrors({ currentPassword: 'รหัสผ่านปัจจุบันไม่ถูกต้อง' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ newPassword: 'รหัสผ่านใหม่ไม่แข็งแรงพอ' });
      } else {
        alert('เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('คุณต้องการออกจากระบบหรือไม่?')) {
      try {
        await signOut(auth);
        navigate('/login');
      } catch (error) {
        alert('เกิดข้อผิดพลาดในการออกจากระบบ: ' + error.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-600 hover:text-gray-800 flex items-center"
          >
            <span className="text-xl mr-2">←</span>
            <span>กลับ</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-800">ตั้งค่า</h1>
        </div>

        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">ข้อมูลโปรไฟล์</h2>
          
          {/* Profile Photo */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              รูปโปรไฟล์
            </label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  src={profileForm.photoURL || '/default-avatar.png'}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  onError={(e) => {
                    e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(profileForm.displayName || 'User');
                  }}
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              <label className="px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition">
                {uploading ? 'กำลังอัปโหลด...' : 'เปลี่ยนรูปภาพ'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>

          {/* Display Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ชื่อ <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="displayName"
              value={profileForm.displayName}
              onChange={handleProfileChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.displayName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="กรอกชื่อของคุณ"
            />
            {errors.displayName && (
              <p className="mt-1 text-sm text-red-500">{errors.displayName}</p>
            )}
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              อีเมล <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={profileForm.email}
              onChange={handleProfileChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="your.email@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เบอร์โทรศัพท์
            </label>
            <input
              type="tel"
              name="phone"
              value={profileForm.phone}
              onChange={handleProfileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="08X-XXX-XXXX"
            />
          </div>
        </div>

        {/* Settings Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">การตั้งค่า</h2>
          
          {/* Theme */}
          <div className="mb-4">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
              <div>
                <span className="font-medium text-gray-700">เลือกธีม</span>
                <p className="text-sm text-gray-500">ปรับแต่งสีธีมของแอพ</p>
              </div>
              <button
                onClick={() => setShowThemeSelector(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                เลือกธีม
              </button>
            </div>
          </div>

          {/* Notifications */}
          <div className="mb-4">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
              <div>
                <span className="font-medium text-gray-700">การแจ้งเตือน</span>
                <p className="text-sm text-gray-500">รับการแจ้งเตือนเกี่ยวกับกิจกรรมต่างๆ</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.notifications}
                  onChange={(e) => handleSettingsChange('notifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
              </label>
            </div>
          </div>

          {/* Language */}
          <div className="mb-4">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
              <div>
                <span className="font-medium text-gray-700">ภาษา</span>
                <p className="text-sm text-gray-500">เลือกภาษาที่ต้องการใช้</p>
              </div>
              <select
                value={settings.language}
                onChange={(e) => handleSettingsChange('language', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="th">ไทย</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
        </div>

        {/* Change Password Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">เปลี่ยนรหัสผ่าน</h2>
            <button
              onClick={() => setShowChangePassword(!showChangePassword)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              {showChangePassword ? 'ยกเลิก' : 'เปลี่ยนรหัสผ่าน'}
            </button>
          </div>

          {showChangePassword && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รหัสผ่านปัจจุบัน
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.currentPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="กรอกรหัสผ่านปัจจุบัน"
                />
                {errors.currentPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.currentPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รหัสผ่านใหม่
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.newPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="กรอกรหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)"
                />
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.newPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ยืนยันรหัสผ่านใหม่
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ยืนยันรหัสผ่านใหม่"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                )}
              </div>

              <button
                onClick={handleChangePassword}
                disabled={saving}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:bg-gray-400"
              >
                {saving ? 'กำลังบันทึก...' : 'เปลี่ยนรหัสผ่าน'}
              </button>
            </div>
          )}
        </div>

        {/* Other Options */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">อื่นๆ</h2>
          
          <div className="space-y-2">
            <Link
              to="/nurse/gifts/history"
              className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">ประวัติของขวัญ</span>
                <span className="text-sm text-gray-500">→</span>
              </div>
            </Link>
            
            <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition">
              <span className="font-medium text-gray-700">เงื่อนไขการใช้งาน</span>
            </button>
            
            <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition">
              <span className="font-medium text-gray-700">นโยบายความเป็นส่วนตัว</span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 bg-red-50 hover:bg-red-100 rounded-lg transition"
            >
              <span className="font-medium text-red-700">ออกจากระบบ</span>
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -mx-4">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:bg-gray-400"
          >
            {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
          </button>
        </div>
      </div>

      {/* Theme Selector Modal */}
      {showThemeSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">เลือกธีม</h3>
              <button
                onClick={() => setShowThemeSelector(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>
            <ThemeSelector onClose={() => setShowThemeSelector(false)} />
          </div>
        </div>
      )}

      <NurseBottomNavigation />
    </div>
  );
};

export default NurseSettingsPage;

